// packages/web/src/components/ScoreBreakdown.tsx — misma lógica (getBreakdown; si falla no renderiza nada).
// Ahora es la versión DOCENTE (el alumno usa learner/LearnerReport). Cambios: tabla con barra
// puntaje/máximo y fila global ponderada; preguntas en tabla compacta con fila expandible para
// la respuesta; chips de criterio con su puntaje 0–25.
import { Fragment, useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { api, type ScoreBreakdownResponse } from '../api';
import { DIM_LABEL, type Dim, type EvidenceCriteria } from '../types';
import { TABLE } from './ui';

const CRITERIA_LABEL: Record<EvidenceCriteria, string> = { precision: 'Precisión', profundidad: 'Profundidad', independencia: 'Independencia', justificacion: 'Justificación' };
const DIM_STYLE: Record<Dim, { n: number; bar: string; text: string }> = {
  recuperacion: { n: 1, bar: 'bg-[#2B5C9E]', text: 'text-[#2B5C9E]' },
  comprension: { n: 2, bar: 'bg-[#0B7A72]', text: 'text-[#0B7A72]' },
  analisis: { n: 3, bar: 'bg-[#6A47A6]', text: 'text-[#6A47A6]' },
  transferencia: { n: 4, bar: 'bg-[#A0530B]', text: 'text-[#A0530B]' },
};
const num = (x: number) => (Math.abs(x) < 10 ? x.toFixed(1) : String(Math.round(x)));
// score_delta 0–25 → misma escala visual que el alumno.
const critTone = (s: number) => (s * 4 >= 75 ? 'bg-emerald-100 text-emerald-900' : s * 4 >= 40 ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-700');
const qTone = (s: number) => (s >= 75 ? 'bg-emerald-600' : s >= 50 ? 'bg-amber-500' : 'bg-red-500');

export function ScoreBreakdown({ sessionId }: { sessionId: string }) {
  const [data, setData] = useState<ScoreBreakdownResponse | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.getBreakdown(sessionId).then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  if (!data) return null;
  const { scores, questions } = data;

  return (
    <div className="space-y-6">
      {scores && (
        <section aria-labelledby="calc-t" className="space-y-3">
          <div>
            <h3 id="calc-t" className="text-base font-semibold text-slate-900">Cómo se calculó el puntaje</h3>
            <p className="text-[13px] text-slate-600 mt-0.5"><strong>Puntaje</strong>: lo obtenido en los criterios evaluados. <strong>Máximo</strong>: lo que esos criterios permitían. <strong>Normalizado</strong>: calidad sobre lo observado, en escala 100 (baja si se observó poco).</p>
          </div>
          <div className={TABLE.wrap}>
            <table className={TABLE.table + ' tabular-nums'}>
              <thead className={TABLE.thead}>
                <tr><th className={TABLE.th}>Dimensión</th><th className={TABLE.th}>Criterios observados</th><th className={TABLE.th + ' w-[28%]'}>Puntaje / máximo</th><th className={TABLE.th + ' text-right'}>Normalizado</th></tr>
              </thead>
              <tbody>
                {scores.dimensions.map((d) => {
                  const S = DIM_STYLE[d.dim];
                  const ratio = d.max_possible > 0 ? (d.raw_score / d.max_possible) * 100 : 0;
                  return (
                    <tr key={d.dim} className={TABLE.tr}>
                      <td className={TABLE.td}><span className={`font-semibold ${S.text}`}>{S.n} · {DIM_LABEL[d.dim]}</span></td>
                      <td className={TABLE.td}>
                        {d.criteria_observed.length > 0
                          ? <span className="flex flex-wrap gap-1">{d.criteria_observed.map((c) => <span key={c} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800">{CRITERIA_LABEL[c]}</span>)}</span>
                          : <span className="text-slate-500">—</span>}
                      </td>
                      <td className={TABLE.td}>
                        <div className="flex items-center gap-2.5">
                          <span className="flex-1 h-1.5 rounded-full bg-slate-100"><span className={`block h-1.5 rounded-full ${S.bar}`} style={{ width: `${Math.min(100, ratio)}%` }} /></span>
                          <span className="text-[13px] text-slate-700 whitespace-nowrap">{num(d.raw_score)} / {num(d.max_possible)}</span>
                        </div>
                      </td>
                      <td className={TABLE.td + ' text-right font-semibold text-slate-900'}>{Math.round(d.normalized_score)}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-slate-300 bg-slate-50">
                  <td className={TABLE.td + ' font-semibold text-slate-900'} colSpan={2}>Global <span className="font-normal text-[13px] text-slate-600">· ponderado por tipo de OA</span></td>
                  <td className={TABLE.td + ' text-[13px] text-slate-700'}>Sin normalizar: {scores.global_raw.toFixed(1)}</td>
                  <td className={TABLE.td + ' text-right text-lg font-bold text-marzano-700'}>{Math.round(scores.global_normalized)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      {questions.length > 0 && (
        <section aria-labelledby="q-t" className="space-y-3">
          <h3 id="q-t" className="text-base font-semibold text-slate-900">Cada pregunta y su puntaje</h3>
          <div className={TABLE.wrap}>
            <table className={TABLE.table}>
              <thead className={TABLE.thead}>
                <tr><th className="w-8" /><th className={TABLE.th}>#</th><th className={TABLE.th}>Pregunta</th><th className={TABLE.th}>Criterios (0–25)</th><th className={TABLE.th + ' text-right'}>Puntaje</th></tr>
              </thead>
              <tbody>
                {questions.map((q) => {
                  const isOpen = open === q.n;
                  const S = q.dim ? DIM_STYLE[q.dim] : null;
                  return (
                    <Fragment key={q.n}>
                      <tr className={TABLE.tr + ' align-top'}>
                        <td className="pl-3 py-3">
                          {q.answer_text && (
                            <button type="button" aria-expanded={isOpen} aria-label={`Ver respuesta de la pregunta ${q.n}`} onClick={() => setOpen(isOpen ? null : q.n)} className="p-0.5 rounded text-slate-500 hover:text-slate-900">
                              <ChevronRight aria-hidden className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                            </button>
                          )}
                        </td>
                        <td className={TABLE.td + ' text-slate-600 tabular-nums'}>{q.n}</td>
                        <td className={TABLE.td + ' max-w-[420px]'}>
                          {S && q.dim && <div className={`text-xs font-semibold ${S.text}`}>{S.n} · {DIM_LABEL[q.dim]}</div>}
                          <p className="text-slate-900">{q.question_text}</p>
                        </td>
                        <td className={TABLE.td}>
                          {q.criteria.length > 0 ? (
                            <span className="flex flex-wrap gap-1">
                              {q.criteria.map((c, i) => <span key={`${c.dim}-${c.criteria}-${i}`} className={`rounded-md px-1.5 py-0.5 text-xs ${critTone(c.score)}`}>{CRITERIA_LABEL[c.criteria]} <strong className="tabular-nums">{c.score}</strong></span>)}
                            </span>
                          ) : <span className="text-slate-500">—</span>}
                        </td>
                        <td className={TABLE.td + ' text-right'}>
                          {q.score !== null ? (
                            <span className="inline-flex flex-col items-end gap-1">
                              <strong className="text-base tabular-nums text-slate-900">{q.score}</strong>
                              <span className="w-14 h-1.5 rounded-full bg-slate-100"><span className={`block h-1.5 rounded-full ${qTone(q.score)}`} style={{ width: `${Math.min(100, Math.max(0, q.score))}%` }} /></span>
                            </span>
                          ) : <span className="text-slate-500">—</span>}
                        </td>
                      </tr>
                      {isOpen && q.answer_text && (
                        <tr className="bg-slate-50">
                          <td /><td />
                          <td colSpan={3} className="px-4 py-3"><span className="text-xs font-semibold text-slate-600">Respuesta del alumno</span><p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{q.answer_text}</p></td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
